import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Square, Volume2, VolumeX, Loader2, SkipBack, SkipForward, ChevronDown, ChevronUp } from 'lucide-react';
import { Tutorial, TutorialStep } from '../types';
import AudioService from '../services/audioService';

interface GlobalAudioPlayerProps {
  tutorial: Tutorial;
  currentStepIndex: number;
  onStepChange: (stepIndex: number) => void;
  className?: string;
  autoPlayOnStepChange?: boolean;
}

interface StepSegment {
  stepIndex: number;
  step: TutorialStep;
  startTime: number;
  endTime: number;
  duration: number;
}

// Sanitize content by removing HTML and Markdown syntax so TTS doesn't read markup
function sanitizeForSpeech(raw: string): string {
  if (!raw) return '';
  let text = raw;
  
  // Remove HTML tags using a temporary element
  try {
    const el = document.createElement('div');
    el.innerHTML = text;
    text = (el.textContent || el.innerText || '').toString();
  } catch (_e) {
    // fallback: strip tags via regex
    text = text.replace(/<[^>]*>/g, ' ');
  }
  
  // Strip common Markdown syntax
  text = text
    // headers, bold/italic, code fences/inline code
    .replace(/(^|\s)(#{1,6}\s*)/g, ' ')
    .replace(/[*_]{1,3}([^*_`]+)[*_]{1,3}/g, '$1')
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1')
    .replace(/~~([^~]+)~~/g, '$1')
    // links/images: [text](url) or ![alt](url) - keep only the alt text
    .replace(/!?\[([^\]]+)\]\([^\)]+\)/g, '$1')
    // HTML img tags - remove completely
    .replace(/<img[^>]*>/gi, '')
    // Common image placeholder patterns
    .replace(/\[image\]/gi, '')
    .replace(/\[img\]/gi, '')
    .replace(/\[placeholder\]/gi, '')
    .replace(/\[photo\]/gi, '')
    .replace(/\[picture\]/gi, '')
    .replace(/\[screenshot\]/gi, '')
    .replace(/\[diagram\]/gi, '')
    .replace(/\[chart\]/gi, '')
    .replace(/\[graph\]/gi, '')
    // blockquotes and lists
    .replace(/^>\s?/gm, '')
    .replace(/^\s*[-+*]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    // tables pipes
    .replace(/\|/g, ' ')
    // excess spaces
    .replace(/\s+/g, ' ')
    .trim();
  return text;
}

const GlobalAudioPlayer: React.FC<GlobalAudioPlayerProps> = ({
  tutorial,
  currentStepIndex,
  onStepChange,
  className = '',
  autoPlayOnStepChange = false
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [currentSegment, setCurrentSegment] = useState<StepSegment | null>(null);
  const [segments, setSegments] = useState<StepSegment[]>([]);
  
  const [voiceoverSettings, setVoiceoverSettings] = useState({
    provider: 'elevenlabs' as 'elevenlabs' | 'browser',
    voice: 'pNInz6obpgDQGcFmaJgB',
    speed: 1.0,
    pitch: 1.0,
    volume: 1.0,
    enabled: true
  });
  
  const speechUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const audioServiceRef = useRef(AudioService.getInstance());

  const loadVoiceoverSettings = () => {
    const saved = localStorage.getItem('voiceoverSettings');
    if (saved) {
      const parsed = JSON.parse(saved);
      setVoiceoverSettings(parsed);
    }
  };

  useEffect(() => {
    loadVoiceoverSettings();
  }, []);

  // Auto-expand while playing/loading; re-collapse when stopped
  useEffect(() => {
    if (isPlaying || isLoading) {
      setIsCollapsed(false);
    }
  }, [isPlaying, isLoading]);

  // Calculate segments for all steps
  useEffect(() => {
    const calculateSegments = () => {
      const newSegments: StepSegment[] = [];
      let currentTime = 0;
      
      tutorial.steps.forEach((step, index) => {
        if (step.content && step.audioEnabled !== false) {
          const sanitized = sanitizeForSpeech(step.content);
          const wordCount = sanitized.split(/\s+/).filter(Boolean).length;
          const duration = (wordCount / 150) * 60; // 150 words per minute
          
          newSegments.push({
            stepIndex: index,
            step,
            startTime: currentTime,
            endTime: currentTime + duration,
            duration
          });
          
          currentTime += duration;
        }
      });
      
      setSegments(newSegments);
      setTotalDuration(currentTime);
    };

    calculateSegments();
  }, [tutorial]);

  // Find current segment based on time
  useEffect(() => {
    if (segments.length === 0) return;
    
    const currentSegment = segments.find(segment => 
      currentTime >= segment.startTime && currentTime < segment.endTime
    );
    
    setCurrentSegment(currentSegment || null);
  }, [currentTime, segments]);

  // Auto-play when step changes (if enabled)
  useEffect(() => {
    if (autoPlayOnStepChange && segments.length > 0) {
      // Small delay to ensure the step change is processed
      const timer = setTimeout(() => {
        handlePlay();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [currentStepIndex, autoPlayOnStepChange]);

  const handlePlay = async () => {
    if (!tutorial.steps.length) return;

    setIsLoading(true);
    
    try {
      if (voiceoverSettings.provider === 'browser') {
        await generateBrowserAudio();
      } else {
        // ElevenLabs via backend AudioService
        const currentStep = tutorial.steps[currentStepIndex];
        if (!currentStep || !currentStep.content || currentStep.audioEnabled === false) {
          throw new Error('No audio content for current step');
        }
        const sanitized = sanitizeForSpeech(currentStep.content);
        
        await audioServiceRef.current.generateAudio(sanitized, {
          voice: voiceoverSettings.voice,
          speed: voiceoverSettings.speed,
          pitch: voiceoverSettings.pitch,
          volume: isMuted ? 0 : voiceoverSettings.volume,
          language: 'en-US',
          provider: 'elevenlabs'
        });
      }
      
      setIsPlaying(true);
      setIsPaused(false);
      startTimeRef.current = Date.now();
      
      // Start progress tracking
      startProgressTracking();
    } catch (error) {
      console.error('Audio playback failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateBrowserAudio = async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!('speechSynthesis' in window)) {
        reject(new Error('Speech synthesis not supported'));
        return;
      }

      // Get text from current step only
      const currentStep = tutorial.steps[currentStepIndex];
      
      if (!currentStep || !currentStep.content || currentStep.audioEnabled === false) {
        reject(new Error('No audio content for current step'));
        return;
      }

      const sanitized = sanitizeForSpeech(currentStep.content);
      const utterance = new SpeechSynthesisUtterance(sanitized);
      speechUtteranceRef.current = utterance;
      
      // Set voice options
      utterance.rate = voiceoverSettings.speed;
      utterance.pitch = voiceoverSettings.pitch;
      utterance.volume = isMuted ? 0 : voiceoverSettings.volume;
      utterance.lang = 'en-US';
      
      // Set the voice if available
      const voices = speechSynthesis.getVoices();
      const selectedVoice = voices.find(voice => voice.name === voiceoverSettings.voice);
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }

      utterance.onstart = () => {
      };

      utterance.onend = () => {
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentTime(0);
        setProgress(0);
        resolve();
      };

      utterance.onerror = (event) => {
        console.error('GlobalAudioPlayer: Speech error:', event);
        reject(new Error('Speech synthesis failed'));
      };

      speechSynthesis.speak(utterance);
    });
  };

  const startProgressTracking = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (speechSynthesis.speaking) {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        setCurrentTime(elapsed);
        
        // Calculate progress based on current step duration
        const currentSegment = segments.find(s => s.stepIndex === currentStepIndex);
        if (currentSegment) {
          const stepProgress = Math.min((elapsed / currentSegment.duration) * 100, 100);
          setProgress(stepProgress);
        }
      }
    }, 100);
  };

  const handlePause = () => {
    if (speechSynthesis.speaking) {
      speechSynthesis.pause();
      setIsPaused(true);
    } else {
      audioServiceRef.current.pauseAudio();
      setIsPaused(true);
    }
  };

  const handleResume = () => {
    if (speechSynthesis.paused) {
      speechSynthesis.resume();
      setIsPaused(false);
    } else {
      audioServiceRef.current.resumeAudio();
      setIsPaused(false);
    }
  };

  const handleStop = () => {
    // Stop any speech synthesis
    if (speechSynthesis.speaking || speechSynthesis.paused) {
      speechSynthesis.cancel();
    } else {
      audioServiceRef.current.stopAudio();
    }
    
    // Reset all states
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentTime(0);
    setProgress(0);
    
    // Clear progress tracking
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleStepBackward = () => {
    if (currentStepIndex > 0) {
      // Stop current speech
      if (speechSynthesis.speaking) {
        speechSynthesis.cancel();
      } else {
        audioServiceRef.current.stopAudio();
      }
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTime(0);
      setProgress(0);
      
      // Change to previous step
      onStepChange(currentStepIndex - 1);
    }
  };

  const handleStepForward = () => {
    if (currentStepIndex < tutorial.steps.length - 1) {
      // Stop current speech
      if (speechSynthesis.speaking) {
        speechSynthesis.cancel();
      } else {
        audioServiceRef.current.stopAudio();
      }
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTime(0);
      setProgress(0);
      
      // Change to next step
      onStepChange(currentStepIndex + 1);
    }
  };

  const handleTimelineClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const targetTime = percentage * totalDuration;
    
    setCurrentTime(targetTime);
    
    // Find the segment at this time and change to that step
    const targetSegment = segments.find(segment => 
      targetTime >= segment.startTime && targetTime < segment.endTime
    );
    
    if (targetSegment && targetSegment.stepIndex !== currentStepIndex) {
      // Stop current speech
      if (speechSynthesis.speaking) {
        speechSynthesis.cancel();
      } else {
        audioServiceRef.current.stopAudio();
      }
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTime(0);
      setProgress(0);
      
      // Change to target step (without auto-play)
      onStepChange(targetSegment.stepIndex);
    }
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!voiceoverSettings.enabled || segments.length === 0) {
    return null;
  }

  // Collapsed (compact) view
  if (isCollapsed) {
    const currentStep = tutorial.steps[currentStepIndex];
    return (
      <div className={`bg-white border border-gray-200 rounded-lg p-2 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 overflow-hidden">
            <button
              onClick={isPlaying ? (isPaused ? handleResume : handlePause) : handlePlay}
              disabled={isLoading}
              className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={isPlaying ? (isPaused ? 'Resume' : 'Pause') : 'Play'}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isPaused ? (
                <Play className="w-4 h-4" />
              ) : isPlaying ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
            </button>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-700 truncate max-w-[36vw]">
                {currentStep?.title}
              </div>
              <div className="text-[10px] text-gray-500">
                {formatTime(currentTime)} / {formatTime(segments.find(s => s.stepIndex === currentStepIndex)?.duration || 0)} • Step {currentStepIndex + 1}/{tutorial.steps.length}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleStop}
              className="flex items-center justify-center w-7 h-7 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors"
              title="Stop"
            >
              <Square className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setIsCollapsed(false)}
              className="flex items-center justify-center w-7 h-7 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
              title="Expand Player"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expanded view
  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-3 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsCollapsed(true)}
            className="flex items-center justify-center w-7 h-7 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors mr-1"
            title="Collapse Player"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            onClick={isPlaying ? (isPaused ? handleResume : handlePause) : handlePlay}
            disabled={isLoading}
            className="flex items-center justify-center w-9 h-9 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <Loader2 className="w-4.5 h-4.5 animate-spin" />
            ) : isPaused ? (
              <Play className="w-4.5 h-4.5" />
            ) : isPlaying ? (
              <Pause className="w-4.5 h-4.5" />
            ) : (
              <Play className="w-4.5 h-4.5" />
            )}
          </button>
          
          <button
            onClick={handleStop}
            className="flex items-center justify-center w-7 h-7 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors"
            title="Stop"
          >
            <Square className="w-3.5 h-3.5" />
          </button>

          <button
            onClick={handleStepBackward}
            disabled={currentStepIndex === 0}
            className="flex items-center justify-center w-7 h-7 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <SkipBack className="w-3.5 h-3.5" />
          </button>

          <button
            onClick={handleStepForward}
            disabled={currentStepIndex === tutorial.steps.length - 1}
            className="flex items-center justify-center w-7 h-7 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <SkipForward className="w-3.5 h-3.5" />
          </button>

          <button
            onClick={handleMuteToggle}
            className="flex items-center justify-center w-7 h-7 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors"
          >
            {isMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          </button>
        </div>

        <div className="text-xs text-gray-600">
          {formatTime(currentTime)} / {formatTime(segments.find(s => s.stepIndex === currentStepIndex)?.duration || 0)}
        </div>
      </div>

      <div className="mb-2">
        <div className="text-sm font-medium text-gray-700 mb-1">
          {tutorial.steps[currentStepIndex]?.title}
        </div>
        <div className="text-xs text-gray-500">
          Step {currentStepIndex + 1} of {tutorial.steps.length}
        </div>
      </div>

      {/* Segmented Timeline */}
      <div className="w-full bg-gray-200 rounded-full h-2 cursor-pointer" onClick={handleTimelineClick}>
        <div className="relative h-full">
          {/* Overall progress bar - shows progress through all steps */}
          <div
            className="bg-blue-600 h-full rounded-full transition-all duration-300"
            style={{ 
              width: `${((currentStepIndex + (progress / 100)) / tutorial.steps.length) * 100}%` 
            }}
          />
          
          {/* Step segments */}
          {segments.map((segment, index) => {
            const segmentStart = (segment.startTime / totalDuration) * 100;
            const segmentWidth = (segment.duration / totalDuration) * 100;
            const isCurrent = segment.stepIndex === currentStepIndex;
            const isActive = currentSegment?.stepIndex === segment.stepIndex;
            
            return (
              <div
                key={segment.stepIndex}
                className={`absolute top-0 h-full border-r-2 ${
                  isActive 
                    ? 'border-blue-400' 
                    : isCurrent 
                      ? 'border-blue-300' 
                      : 'border-gray-300'
                }`}
                style={{
                  left: `${segmentStart}%`,
                  width: `${segmentWidth}%`
                }}
                title={`Step ${segment.stepIndex + 1}: ${segment.step.title}`}
              />
            );
          })}
        </div>
      </div>

      {/* Step indicators */}
      <div className="flex justify-between mt-1 text-[10px] text-gray-500">
        {segments.map((segment, index) => {
          const isCurrent = segment.stepIndex === currentStepIndex;
          const isCompleted = segment.stepIndex < currentStepIndex;
          
          return (
            <div
              key={segment.stepIndex}
              className={`px-2 py-1 rounded transition-colors ${
                isCurrent 
                  ? 'bg-blue-500 text-white font-semibold' 
                  : isCompleted
                    ? 'bg-green-100 text-green-700'
                    : 'hover:bg-gray-100 text-gray-500'
              }`}
              title={segment.step.title}
            >
              {segment.stepIndex + 1}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GlobalAudioPlayer;
